import { useEffect, useMemo, useState } from 'react';
import { Button, MenuItem, TextField } from '@mui/material';
import { INVOICE_TYPE_SUPPLIER, InvoiceType, SELLER_BANK_ACCOUNT_BLOCKED_CODE } from '@sr/dto';
import { useNavigate } from 'react-router-dom';
import { useSnack } from 'shared/ui/snack';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { invoiceDataApi } from 'entities/invoice/details/api';
import { Dialog } from 'shared/ui/dialog';
import { useBankDetailsList } from 'hooks/bank-details.hook';

type Props = {
  invoiceId: number;
  invoiceType: InvoiceType;
  sellerLegalEntityId?: number;
};

const isSellerBankAccountBlockedError = (e: unknown): boolean => {
  const data = (e as { data?: unknown })?.data;
  return !!data && typeof data === 'object' && (data as { code?: string }).code === SELLER_BANK_ACCOUNT_BLOCKED_CODE;
};

export const CopyInvoiceButton = ({ invoiceId, invoiceType, sellerLegalEntityId }: Props) => {
  const [update, { isLoading }] = invoiceDataApi.useCopyInvoiceMutation();
  const [invoiceNumberDialogOpen, setInvoiceNumberDialogOpen] = useState(false);
  const [bankDetailsDialogOpen, setBankDetailsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnack();
  const showConfirmDialog = useConfirmDialog();

  const copy = async (params?: { invoiceNumber?: string; sellerBankDetailsId?: number }) => {
    if (isLoading) return;
    try {
      const res = await update({ invoiceId, ...params }).unwrap();
      showSuccess('Счет скопирован');
      navigate('/invoice/' + res.invoiceId);
      return true;
    } catch (e: unknown) {
      if (isSellerBankAccountBlockedError(e)) {
        setBankDetailsDialogOpen(true);
        return false;
      }
      showError('Не удалось создать копию счета', e);
      return false;
    }
  };

  const handleCopy = () => {
    if (isLoading) return;
    if (invoiceType === INVOICE_TYPE_SUPPLIER) {
      setInvoiceNumberDialogOpen(true);
      return;
    }

    showConfirmDialog({ title: 'Вы уверены?', content: 'Создать копию счета?' }).then(() => copy());
  };

  const handleSupplierCopy = async (invoiceNumber: string) => {
    const copied = await copy({ invoiceNumber });
    if (copied) setInvoiceNumberDialogOpen(false);
  };

  const handleBankDetailsCopy = async (sellerBankDetailsId: number) => {
    const copied = await copy({ sellerBankDetailsId });
    if (copied) setBankDetailsDialogOpen(false);
  };

  return (
    <>
      <Button variant="contained" onClick={handleCopy} disabled={isLoading}>Копия</Button>
      <SupplierInvoiceCopyDialog
        open={invoiceNumberDialogOpen}
        isLoading={isLoading}
        onClose={() => setInvoiceNumberDialogOpen(false)}
        onSubmit={handleSupplierCopy}
      />
      <SelectSellerBankDetailsDialog
        open={bankDetailsDialogOpen}
        isLoading={isLoading}
        sellerLegalEntityId={sellerLegalEntityId}
        onClose={() => setBankDetailsDialogOpen(false)}
        onSubmit={handleBankDetailsCopy}
      />
    </>
  );
};

type SupplierInvoiceCopyDialogProps = {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (invoiceNumber: string) => Promise<void>;
};

const SupplierInvoiceCopyDialog = ({ open, isLoading, onClose, onSubmit }: SupplierInvoiceCopyDialogProps) => {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const trimmedInvoiceNumber = invoiceNumber.trim();
  const hasError = submitted && !trimmedInvoiceNumber;

  useEffect(() => {
    if (!open) {
      setInvoiceNumber('');
      setSubmitted(false);
    }
  }, [open]);

  const handleSubmit = () => {
    setSubmitted(true);
    if (!trimmedInvoiceNumber) return;
    onSubmit(trimmedInvoiceNumber);
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <Dialog.Title>Номер входящего счета</Dialog.Title>
      <Dialog.Content>
        <TextField
          autoFocus
          fullWidth
          required
          label="Номер счета"
          value={invoiceNumber}
          error={hasError}
          helperText={hasError ? 'Укажите номер счета' : undefined}
          disabled={isLoading}
          onChange={(event) => setInvoiceNumber(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSubmit();
          }}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="contained" onClick={handleSubmit} disabled={isLoading || !trimmedInvoiceNumber}>
          Создать копию
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Отмена
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

type SelectSellerBankDetailsDialogProps = {
  open: boolean;
  isLoading: boolean;
  sellerLegalEntityId?: number;
  onClose: () => void;
  onSubmit: (sellerBankDetailsId: number) => Promise<void>;
};

const SelectSellerBankDetailsDialog = ({ open, isLoading, sellerLegalEntityId, onClose, onSubmit }: SelectSellerBankDetailsDialogProps) => {
  const { collection } = useBankDetailsList(sellerLegalEntityId);
  const [selectedId, setSelectedId] = useState<number | ''>('');

  const options = useMemo(
    () => collection.items.filter((item) => item.outgoingInvoicesAllowed !== false),
    [collection.items],
  );

  useEffect(() => {
    if (!open) setSelectedId('');
  }, [open]);

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <Dialog.Title>Выберите другой расчётный счёт</Dialog.Title>
      <Dialog.Content>
        <TextField
          select
          fullWidth
          label="Реквизиты продавца"
          value={selectedId}
          disabled={isLoading || collection.isLoading}
          helperText={
            options.length === 0
              ? 'Нет реквизитов с активным расчётным счётом, разрешённым для исходящих счетов'
              : 'Текущий расчётный счёт заблокирован для исходящих счетов'
          }
          onChange={(event) => setSelectedId(Number(event.target.value))}
        >
          {options.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.bankName}, р/с {item.accountNumber} (БИК {item.bik})
            </MenuItem>
          ))}
        </TextField>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="contained" onClick={() => selectedId && onSubmit(Number(selectedId))} disabled={isLoading || !selectedId}>
          Создать копию
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Отмена
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
